import {isPlatformBrowser} from '@angular/common';
import {Component, effect, inject, Input, OnDestroy, OnInit, PLATFORM_ID, signal} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {IonButton, IonContent, IonIcon, IonSpinner} from '@ionic/angular/standalone';
import {ContentType} from '@types';
import {HeaderComponent} from '../header/header.component';
import {addIcons} from 'ionicons';
import {shareOutline} from 'ionicons/icons';
import {
	MarkdownContent,
	MarkdownContentService,
	SeoService,
	ToastService,
	TranslateHelperService,
	type ArticleStructuredData
} from '@services';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {environment} from '../../../environments/environment';

@Component({
	           selector: 'app-markdown-viewer',
	           standalone: true,
	           templateUrl: './markdown-viewer.component.html',
	           imports: [
		           HeaderComponent,
	           IonContent,
	           IonButton,
	           IonIcon,
	           IonSpinner,
	           RouterLink
	           ],
	           styleUrls: ['./markdown-viewer.component.scss']
           })
export class MarkdownViewerComponent implements OnInit, OnDestroy {
	@Input() contentType: ContentType = 'blog';
	@Input() slug?: string;
	@Input() backRoute?: string;
	@Input() backLabel?: string;

	private readonly route = inject(ActivatedRoute);
	private readonly markdownService = inject(MarkdownContentService);
	private readonly seoService = inject(SeoService);
	private readonly toastService = inject(ToastService);
	public readonly translateService = inject(TranslateHelperService);
	private readonly sanitizer = inject(DomSanitizer);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly isBrowser = isPlatformBrowser(this.platformId);
	private readonly language = this.translateService.language;
	private readonly slugValue = signal<string>('');

	readonly content = signal<{html: SafeHtml; metadata: MarkdownContent['metadata']} | null>(null);
	loading = true;
	error = false;

	constructor() {
		addIcons({shareOutline});
	}

	private readonly loadContentEffect = effect((onCleanup) => {
		const slug = this.slugValue();
		this.language();
		if (!slug) {
			return;
		}

		let canceled = false;
		this.loading = true;
		this.error = false;
		onCleanup(() => {
			canceled = true;
		});

		this.markdownService.clearCacheFor(slug, this.contentType);

		const loader = this.contentType === 'blog'
			? this.markdownService.loadBlogContent(slug)
			: this.markdownService.loadPracticeContent(slug);

		void loader
			.then((content) => {
				if (canceled) {
					return;
				}
				const trustedHtml = this.sanitizer.bypassSecurityTrustHtml(content.html);
				this.content.set({
					html: trustedHtml,
					metadata: content.metadata || {}
				});
				this.loading = false;
				this.error = false;
				if (content.metadata) {
					this.seoService.update({
						title: content.metadata.title,
						description: content.metadata.description,
						keywords: content.metadata.keywords,
						path: `/${this.contentType === 'blog' ? 'blog' : 'bdsm-practices'}/${slug}`,
						imageUrl: content.metadata.imageUrl
					});
					if (this.contentType === 'blog') {
						const path = `/blog/${slug}`;
						const articleData: ArticleStructuredData = {
							headline: content.metadata.title ?? slug,
							description: content.metadata.description ?? '',
							path,
							imageUrl: content.metadata.imageUrl,
							datePublished: content.metadata.date,
							dateModified: content.metadata.dateModified ?? content.metadata.date
						};
						this.seoService.setArticleStructuredData(articleData);
					} else {
						this.seoService.clearArticleStructuredData();
					}
				}
			})
			.catch(() => {
				if (canceled) {
					return;
				}
				this.loading = false;
				this.error = true;
				if (this.contentType === 'blog') {
					this.seoService.clearArticleStructuredData();
				}
			});
	});

	ngOnInit(): void {
		// Get slug from input first
		let slug = this.slug;

		if (!slug) {
			// Try to get from route params
			slug = this.route.snapshot.paramMap.get('slug') || '';

			// If still no slug, try to get from URL path
			if (!slug && this.route.snapshot.url.length > 0) {
				const urlSegments = this.route.snapshot.url;
				// For blog routes: /blog/slug
				// For practice routes: /bdsm-practices/slug
				if (urlSegments.length >= 2) {
					slug = urlSegments[urlSegments.length - 1].path;
				}
			}
		}

		if (!slug) {
			this.error = true;
			this.loading = false;
			return;
		}

		this.slugValue.set(slug);

		// Set default back route and label
		if (!this.backRoute) {
			this.backRoute = this.contentType === 'blog' ? '/blog' : '/bdsm-practices-dictionary';
		}
		if (!this.backLabel) {
			this.backLabel = this.contentType === 'blog' ? '← Retour au blog' : '← Retour au dictionnaire';
		}
	}

	ngOnDestroy(): void {
		this.seoService.clearArticleStructuredData();
	}

	async shareContent(): Promise<void> {
		if (!this.isBrowser || !this.slugValue()) {
			return;
		}

		const content = this.content();
		const shareTitle = content?.metadata?.title ?? (this.contentType === 'blog' ? 'Article' : 'Pratique BDSM');
		const shareText = content?.metadata?.description ?? 'Découvre ce contenu sur Slaveous';
		const shareUrl = this.getShareUrl();

		try {
			if (navigator.share) {
				await navigator.share({
					title: shareTitle,
					text: shareText,
					url: shareUrl
				});
				return;
			}

			await navigator.clipboard.writeText(shareUrl);
			this.toastService.showToastSuccess('Lien copié dans le presse-papier');
		} catch (error: unknown) {
			if (error instanceof DOMException && error.name === 'AbortError') {
				return;
			}
			this.toastService.showToastError('Impossible de partager ce contenu pour le moment');
		}
	}

	private getShareUrl(): string {
		const slug = this.slugValue();
		if (!slug) {
			return '';
		}

		const path = this.contentType === 'blog' ? `/blog/${slug}` : `/bdsm-practices/${slug}`;
		const localizedPath = this.translateService.localize(path);
		const canonicalHost = environment.publicBaseUrl.replace(/\/+$/, '');
		return `${canonicalHost}${localizedPath}`;
	}

	getSectionRoute(): string {
		return this.contentType === 'blog' ? '/blog' : '/bdsm-practices-dictionary';
	}

	getSectionLabel(): string {
		return this.contentType === 'blog' ? 'Blog' : 'Pratiques';
	}

	getCurrentRoute(): string {
		const slug = this.slugValue();
		if (!slug) {
			return this.getSectionRoute();
		}
		return this.contentType === 'blog' ? `/blog/${slug}` : `/bdsm-practices/${slug}`;
	}

	getCurrentLabel(): string {
		const title = this.content()?.metadata?.title?.trim();
		if (title) {
			return title;
		}
		return this.slugValue().replace(/-/g, ' ');
	}
}
